@use 'sass:math';
/*
# Settings (_settings)
*/

/*
## Variables

### General

$phi: 1.618033988749 - Golden ratio
*/
$phi: 1.618033988749;

/*
### Colours

<span style="color: #333">$colour-base - #333</span> <span style="background: #333; color: #FFF;">&nbsp;$colour-base - #333&nbsp;</span>

<span style="color: #339">$colour-highlight - #339</span> <span style="background: #339; color: #FFF;">&nbsp;$colour-highlight - #339&nbsp;</span>

<span style="color: #339">$colour-link - #339</span> <span style="background: #339; color: #FFF;">&nbsp;$colour-link - #339&nbsp;</span>
*/
$colour-background: 		#FFF;

// Generic colour setup //
$colour-black: #000000;
$colour-base: 				#434343;
$colour-base-contr: 		#FFF;

$core-neutral-900: #000000;
$core-neutral-700: #5B5656;
$core-neutral-400: #9B9393;
$core-neutral-300: #E8E4E4;
$core-neutral-200: #F5F2F2;
$core-neutral-100: #FFFFFF;

$colour-primary-100: 		#EFE9F1;
$colour-primary-300: 		#BFA7C9;
$colour-primary-500: 		#5F2478;
$colour-primary-700:        #4c1564;

$colour-secondary-200:      #BEE6E9;

$colour-highlight: 			#671375;
$colour-highlight-contr: 	#FFF;

$colour-highlight-lt: 			#e8dcea;
$colour-highlight-lt-contr: 	#FFF;


$colour-alt: 				#26acb5;
$colour-alt-contr: 			#333;

$colour-dk: 				#262626;
$colour-dk-contr: 			#FFF;

$colour-active: 		#26acb5;
$colour-active-dk: 		darken($colour-active, 10%);
$colour-active-lt: 		lighten($colour-active, 50%);
$colour-weight: 		#671375;
$colour-weight-dk: 		darken($colour-weight, 10%);
$colour-weight-lt: 		lighten($colour-weight, 68%);
$colour-smoking: 		#cc2827;
$colour-smoking-dk: 	darken($colour-smoking, 10%);
$colour-smoking-lt: 	lighten($colour-smoking, 45%);
$colour-drink: 			#fab407;
$colour-drink-dk: 		darken($colour-drink, 10%);
$colour-drink-lt: 		lighten($colour-drink, 45%);


// Specific colour binding //
$colour-input-border: 		#CCC;
$colour-link: 				#26acb5;

$secondary-green: #469638;
$secondary-orange: #E74E1D;

// Section colours //
$colour_site_bg: 				$colour-base-contr;
$colour_site_content: 			$colour-base;

$colour_header_bg: 				$colour-base-contr;
$colour_header_content: 		$colour-base;

$colour_breadcrumbs_bg: 		$colour-alt;
$colour_breadcrumbs_content: 	$colour-alt-contr;

$colour_content_bg: 			$colour-base-contr;
$colour_content_content: 		$colour-base;

$colour_footer_bg: 				$colour-base-contr;
$colour_footer_content: 		$colour-base;

// Generic reusable variables to maintain coherent appearance //
$border-radius: 0;
$spacing: 1.4rem;

/*
### Fonts

$font-size - 13

$line-height - 1.4

<span style="font-family: sans-serif;">$font-base - sans-serif</span>

<span style="font-family: sans-serif;">$font-header - sans-serif</span>
*/
$font-size-sm:  10;
$font-size: 	16;
$font-size-lg: 	20;
$font-size-xl: 	30;

$line-height: 	1.4;

$font-base: 	'TodaySB', sans-serif;
$font-header: 	'TodaySB', sans-serif;
$font-header-bold: 	'TodaySB-Bold', sans-serif;
$font-header-medium: 	'TodaySB-Medium', sans-serif;

$very-light-drop-shadow: 0px 2px 8px rgba(0, 0, 0, 0.04);
$light-drop-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
$heavy-drop-shadow: 0px 2px 12px rgba(0, 0, 0, 0.2);
$search-drop-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);



/*
## Functions

### em($size: $font-size, $context: $fs)

Will convert a pixel based size to an em value.
First value is the target size, the second value is the font-size of the context it is in.

Examples:
`em(26)` // 2em if base font-size is 13px
`em(18, 12)` // 1.5em
*/
@function em($size: $font-size, $context: $font-size) {
	@return math.div($size, $context) + em;
}

/*
### rem($size: $font-size, $context: $fs)

Will convert a pixel based size to an rem value.
First value is the target size, the second value is the font-size of the context it is in.

Examples:
`em(26)` // 2em if base font-size is 13px
`em(18, 12)` // 1.5em
*/
@function rem($size: $font-size, $context: 16) {
	@return math.div($size, $context) + rem;
}

/*
### lh($size: $font-size, $context: $fs)

Will provide the same function as 'em' above buth without appending 'em' to the result.
This makes it more suitable to use as a line-height value.

Examples:
`lh(26)` // 2 if base font-size is 13px
`lh(18, 12)` // 1.5
*/
@function lh($size: $font-size, $context: $font-size) {
	@return math.div($size, $context);
}

/*
## Mixins

Mixins must to be called using @include (scss) or + (sass)
*/

/*
### max($maxwidth: $width)

A simple max-width media query

<pre>
div {
	@include max(768px) {
		display: none;
	}
}
</pre>

Outputs:
<pre>
@media (max-width: 768px) {
	div {
		display: none;
	}
}
</pre>
*/
@mixin max($maxwidth: $width) {
	@media (max-width: $maxwidth) {
		@content
	}
}

/*
### min($minwidth: $width)

A simple min-width media query

<pre>
div {
	@include min(768px) {
		display: block;
	}
}
</pre>

Outputs:
<pre>
@media (min-width: 768px) {
	div {
		display: block;
	}
}
</pre>
*/
@mixin min($minwidth: $width) {
	@media (min-width: $minwidth) {
		@content
	}
}

/*
### pixel-ratio($pixelratio: 2, $basedpi: 96)

A simple pixel-ratio media query

$basedpi is used for fine control over the dpi query value

<pre>
div {
	@include pixel-ratio {
		background-image: url(image@2x.png);
	}
}
</pre>

Outputs:
<pre>
@media
	(-webkit-min-device-pixel-ratio: 2),
	(   min--moz-device-pixel-ratio: 2),
	(     -o-min-device-pixel-ratio: 2/1),
	(        min-device-pixel-ratio: 2),
	(                min-resolution: 192dpi),
	(                min-resolution: 2dppx) {
		div {
			background-image: url(image@2x.png);
		}
	}
</pre>
*/
@mixin pixel-ratio($pixelratio: 2, $basedpi: 96) {
	@media
		(-webkit-min-device-pixel-ratio: $pixelratio),
		(   min--moz-device-pixel-ratio: $pixelratio),
		(     -o-min-device-pixel-ratio: $pixelratio/1),
		(        min-device-pixel-ratio: $pixelratio),
		(                min-resolution: #{$basedpi*$pixelratio}dpi),
		(                min-resolution: #{$pixelratio}dppx) {
			@content
		}
}

/*
### Placeholders
*/
@mixin placeholder {
	:-moz-placeholder {
		@content
	}
	::-moz-placeholder {
		@content
	}
	::-webkit-input-placeholder {
		@content
	}
	:-ms-input-placeholder {
		@content;
		opacity: 1 !important;
	}
}


/*
### boxes($cols: 3, $gutter: 10px, $selector: ".col")

Set the container and its children (as  selected by the selector argument) to be a set of columns.
*/
@mixin boxes($cols: 3, $gutter: 10px, $selector: ".col") {
	margin-left: -$gutter;
	margin-right: -$gutter;

	#{$selector} {
		float: left;
		padding-left: $gutter;
		padding-right: $gutter;
		width: math.div(100%, $cols);

		&:nth-child(n) {
			clear: none;
		}

		&:nth-child(#{$cols}n+1) {
			clear: left;
		}
	}
}


/*
## Extend

Use these placeholder styles with @extend.
*/

/*
### %clearfix

@extend interface for @include clearfix;
*/
%clearfix {
	&:before, &:after {
		content: "";
		display: table;
	}

	&:after { clear: both; }
}

/*
### %list-reset

@extend interface for @include list-reset;
*/
%list-reset {
	margin: 0;
	padding: 0;

	> li {
		list-style: none;
		list-style-image: none;
	}
}


/*
### %list-reset-full

@extend interface for @include list-reset-full;
*/
%list-reset-full {
	&, ul, ol {
		margin: 0;
		padding: 0;
	}

	li {
		list-style: none;
		list-style-image: none;
	}
}